"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useState } from "react";

const SWATCHES = Array.from({ length: 18 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);

/** Normalise the URL `?paper=` value to the picker's two-digit form so a
 *  shared link with `?paper=1` highlights the same swatch as `?paper=01`. */
function normaliseCurrent(raw: string | null): string | null {
  if (raw === null) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 18) return null;
  return String(n).padStart(2, "0");
}

export function PaperPicker() {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const current = normaliseCurrent(params.get("paper"));
  const opacity = params.get("paperOpacity") ?? "0.25";
  const [open, setOpen] = useState(false);

  // Gate the picker behind a debug flag — design-review tool, not a
  // user-facing surface. Reach via `?debug=1`.
  if (params.get("debug") !== "1") return null;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params.toString());
    if (value === null) next.delete(key);
    else next.set(key, value);
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[70] cs-mono">
      {open ? (
        <div className="cs-card p-3 w-[260px] shadow-lg" role="group" aria-label="paper texture picker">
          <div className="flex items-center justify-between mb-2">
            <span className="cs-label">paper</span>
            <button
              onClick={() => setOpen(false)}
              className="text-ink-3 hover:text-signal text-xs"
              aria-label="close paper picker"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-6 gap-1.5 mb-3">
            <button
              onClick={() => setParam("paper", null)}
              aria-label="default paper texture"
              aria-pressed={!current}
              className={`h-8 rounded-sm border ${
                !current ? "border-signal" : "border-rule-2 hover:border-ink"
              } bg-bg flex items-center justify-center text-[11px] text-ink-3`}
            >
              ∅
            </button>
            {SWATCHES.map((n) => (
              <button
                key={n}
                onClick={() => setParam("paper", n)}
                aria-label={`paper texture ${n}`}
                aria-pressed={current === n}
                className={`h-8 rounded-sm border bg-cover bg-center ${
                  current === n
                    ? "border-signal ring-1 ring-signal"
                    : "border-rule-2 hover:border-ink"
                }`}
                style={{
                  backgroundImage: `url("/textures/grainy/${n}.jpg")`,
                }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-ink-3 uppercase tracking-wider" id="paper-opacity-label">
              opacity
            </span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={opacity}
              aria-labelledby="paper-opacity-label"
              onChange={(e) =>
                setParam(
                  "paperOpacity",
                  e.target.value === "0.25" ? null : e.target.value,
                )
              }
              className="flex-1"
            />
            <span className="text-[11px] text-ink-3 tabular-nums w-8 text-right">
              {opacity}
            </span>
          </div>
          <div className="mt-2 text-[11px] text-ink-3">
            {current ? `grainy/${current}` : "default"}
          </div>
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          aria-label={`open paper picker${current ? `, current texture: ${current}` : ""}`}
          aria-expanded={false}
          className="cs-card px-3 py-1.5 text-[11px] uppercase tracking-wider hover:border-ink"
        >
          paper {current ? `· ${current}` : ""}
        </button>
      )}
    </div>
  );
}
